export const en = {
  seo: {
    landing: {
      title: "Live AI copilot for English, Portuguese, and Spanish meetings",
      description:
        "Minuta listens to your meeting, detects when a question is aimed at you, and drafts a clear answer in English, Portuguese, or Spanish based on your documents and what was already said.",
    },
    downloads: {
      title: "Download Minuta for macOS and Windows",
      description:
        "Download the Minuta desktop app for macOS and Windows. Local audio capture, fast cloud transcription, real-time translation, automatic meeting summary, and an AI copilot inside the meeting.",
    },
    signup: { title: "Create your account", description: "Create your free Minuta account." },
    confirm: { title: "Confirm your email", description: "Confirm your email to finish creating your Minuta account." },
    done: { title: "Account ready", description: "Your Minuta account is ready." },
    login: { title: "Sign in", description: "Sign in to your Minuta account." },
    account: { title: "My account", description: "Your Minuta account and plan." },
    billingDone: { title: "Payment complete", description: "Your Minuta Pro plan is being activated." },
    billingCancelled: { title: "Purchase cancelled", description: "Your Minuta subscription purchase was cancelled." },
    launch: {
      title: "Minuta on Product Hunt — 30% off Pro for life",
      description:
        "Stop freezing when the question lands. Secure multilingual meeting copilot for Mac. 30% off Pro with code HUNT30 — only on Product Hunt day.",
      ogImage: "/og/launch-en.png",
    },
    privacy: {
      title: "Privacy Policy",
      description:
        "How Minuta handles your meeting data: core transcription runs on your computer; optional account, billing, web search, and translation features may use external services when enabled.",
    },
    terms: {
      title: "Terms of Service",
      description:
        "Terms of service for using Minuta — the secure AI copilot for meetings on macOS and Windows. Account, subscription, acceptable use, and liability.",
    },
  },
  nav: {
    copilot: "Copilot",
    howItWorks: "How it works",
    pricing: "Pricing",
    faq: "FAQ",
    blog: "Blog",
    signIn: "Sign in",
    getStarted: "Get started free",
  },
  cookieConsent: {
    ariaLabel: "Cookie consent",
    kicker: "Privacy choice",
    title: "Cookies and privacy",
    body:
      "We use essential storage to run the site and, only with your consent, optional cookies or similar technologies for product measurement and checkout support. This choice supports LGPD and GDPR requirements for visitors in Brazil and Europe.",
    privacyLink: "Read the privacy policy",
    reject: "Decline",
    accept: "Accept",
  },
  hero: {
    badge: "Live copilot · EN/PT/ES meetings · no bot",
    h1Line1: "Minuta listens to the call.",
    h1Line2: "You know what to say.",
    description:
      "The moment a question is aimed at you, Minuta catches the language and drafts a clear answer in English, Portuguese, or Spanish — grounded in your documents and everything already said. You read it and speak with confidence.",
    ctaPrimary: "Download free trial",
    ctaPrimarySub: "macOS · Windows",
    ctaSecondary: "See the copilot",
    ctaSecondarySub: "question → answer",
    footnote: "macOS · Windows · 7-day free trial",
  },
  whereAudioGoes: {
    label: "Trust layer",
    heading: "Local capture. Cloud AI chain. No bot in the meeting.",
    description:
      "Minuta stays a desktop app for system audio capture, then sends the minimum required meeting data through reviewed cloud paths for transcription, summaries, speaker ID, and copilot answers.",
    privateLink: "Privacy policy",
    noBotLink: "No-bot setup",
    steps: [
      {
        title: "Your computer captures the call",
        body: "Zoom, Teams, Google Meet, and browser calls keep running normally. Minuta listens from your machine; nothing joins as a participant.",
      },
      {
        title: "Cloud AI chain processes the audio",
        body: "Deepgram transcribes the audio (OpenAI Whisper and Groq run as automatic fallback). OpenAI drafts summaries and copilot answers (Anthropic Claude Haiku and Groq run as fallback). Modal runs pyannote for speaker detection. Payloads are narrow by design and reviewed in code.",
      },
      {
        title: "You keep the useful record",
        body: "The transcript, speaker labels, summary, and copilot answers stay locally in the desktop app — ready to review, export, or delete.",
      },
    ],
    caveat:
      "Meeting audio is captured locally, processed through a reviewed multi-provider cloud chain, and saved back to your desktop app.",
  },
  leadMagnet: {
    badge: "Lead magnet",
    heading: "Get the 5 practical tricks to perform better in technical English calls",
    description:
      "A short, field-tested playbook for ESL engineers, consultants, and sales engineers: what to prepare before the meeting, what to do when you freeze, and how to answer clearly under pressure.",
    emailLabel: "Work email",
    emailPlaceholder: "you@company.com",
    cta: "Send me the guide",
    ctaLoading: "Sending...",
    privacyNote: "We only use your email for this guide and future Minuta updates. Unsubscribe anytime.",
    success: "Guide requested. Check your inbox in the next few minutes.",
    error: "We couldn't submit your email right now. Please try again in a moment.",
    invalidEmail: "Please enter a valid email address.",
  },
  showcase: {
    slides: [
      {
        tag: "No bot in the call",
        heading: "Minuta stays on your screen, not in the participant list",
        body: "Use it with Google Meet, Zoom, or Teams without adding a notetaker to the meeting. The app listens from your desktop and stays available while the conversation happens.",
        alt: "Google Meet open with Minuta's mini control listening to the meeting without adding a bot to the call.",
      },
      {
        tag: "Live copilot",
        heading: "The question appears. So does the answer.",
        body: "When someone asks a difficult question in English, Portuguese, or Spanish, Minuta detects the moment and suggests a clear answer you can read, adjust, or say right away.",
        alt: "Minuta live panel over a Google Meet call, showing a detected question and a suggested copilot answer.",
      },
      {
        tag: "Before the meeting",
        heading: "Prepare language, context, and capture before the call",
        body: "Open Minuta before the meeting, choose the language, turn on translation if you need it, and add context so the copilot can answer with more precision.",
        alt: "Minuta start screen for configuring the session, language, translation, microphone, and context before listening.",
      },
    ],
  },
  problem: {
    heading: "You know this stuff. You just don't always show it in the moment.",
    body1:
      "You're on a client call. They ask something specific about a stack you've shipped a dozen times — and the name of the service just won't come. Or you're explaining an architecture and you know there's a clearer way to say it, but under pressure it comes out messy.",
    body2:
      "It's not a knowledge gap. It's the friction between knowing and performing in real time.",
  },
  englishBarrier: {
    label: "Multilingual meetings",
    heading: "You understand the work. Now answer in the language of the room.",
    body1:
      "Your technical judgment is already there. The hard part is the live moment: someone asks in English, Portuguese, or Spanish, the room waits, and you need the answer to come out clearly before the conversation moves on.",
    body2:
      "Minuta closes that gap with a real-time transcript, optional translation between English, Portuguese, and Spanish, and a suggested answer you can read and say with confidence in the right language.",
    bullets: [
      "Live translation across English, Portuguese, and Spanish while the other side talks",
      "Didn't catch that? The transcript is right there — scroll back without breaking the call",
      "Copilot drafts the answer in the meeting language, at your technical level",
      "Works on Zoom, Teams, Google Meet — no bot joins, nobody sees it",
    ],
  },
  useCases: {
    label: "Use cases",
    heading: "The question lands. The answer is already forming.",
    subheading:
      "Real moments from technical calls in English, Portuguese, and Spanish — and what the copilot suggests you say.",
    saidLabel: "They asked:",
    sayLabel: "Say this:",
    items: [
      {
        tab: "Technical call",
        scenario: "Mid-call, the client asks a pointed architecture question.",
        said: "How would you handle retries and idempotency if that payment webhook fires twice?",
        say: "I'd make the handler idempotent — key each event by its provider ID and skip anything already processed. Retries become safe by design: a double-fire just hits the dedup check. I set this up on a billing integration last year.",
      },
      {
        tab: "Demo / objection",
        scenario: "During the demo, the client compares the product with a competitor.",
        said: "This looks fine, but Competitor X already does it and we're not paying extra for that.",
        say: "Fair — the overlap is real. The difference is where it runs: ours keeps the data on your side and never adds a bot to the call. If that's not a constraint for you, X is reasonable; if it is, that's the line.",
      },
      {
        tab: "Team sync",
        scenario: "Sync with an international team. They ask for status and you answer live.",
        said: "Where are we on the migration — is it still on track for this sprint?",
        say: "The migration is on track. Schema changes are merged and tested; what's left is the backfill, which runs tonight. If it comes back clean, we close this tomorrow — no blockers right now.",
      },
    ],
  },
  features: {
    label: "Under the hood",
    heading: "Lightweight and fast, running silently.",
    items: [
      {
        title: "Fast cloud processing",
        description:
          "Audio capture stays local on your desktop, but transcription, speaker ID, and copilot run over secure, zero-retention cloud paths using fast cloud models. The heavy models are gone from your laptop.",
        tag: "Speed",
      },
      {
        title: "Transcribes, translates, and identifies speakers",
        description:
          "Detects voice in real time, automatically transcribes English, Portuguese, and Spanish, and translates when you need a second language on screen. When you stop, it identifies who spoke each segment — no manual steps.",
        tag: "Pipeline",
      },
      {
        title: "Automatic meeting summary with tasks",
        description:
          "As soon as you stop recording, the AI generates a complete meeting summary: executive summary, key points, and identified action items. Ready to paste into Notion, Obsidian, or email.",
        tag: "Output",
      },
    ],
  },
  copilot: {
    badge: "Pro only",
    heading: "Your copilot inside the meeting",
    description:
      "This is what Minuta is really about. The copilot listens to the meeting and automatically detects when a question is directed at you. One click gives you a suggested response grounded in your documents, what's already been said in the meeting, and real-time web search. Available on the Pro plan only.",
    bullets: [
      "Automatically detects questions in the live transcript",
      "Suggested responses based on your documents + meeting transcript",
      "Real-time web search to enrich the answer (optional)",
      "Load PDFs, notes, or texts as context before starting",
    ],
    mockup: {
      barLabel: "Copilot",
      liveLabel: "Live transcript",
      questionBadge: "Question detected",
      ctaButton: "What to say",
      responseBadge: "Suggested response",
      addFile: "Add file",
      sourcesLabel: "Sources:",
      transcript: [
        { time: "14:21", speaker: "Maria", text: "Let's go over the next steps for the proposal." },
        { time: "14:38", speaker: "Carlos", text: "The timeline you mentioned earlier — does that still hold?" },
      ],
      detectedQ: {
        time: "14:52",
        speaker: "João",
        text: "What makes your product different from competitor X?",
      },
      responseText:
        "Based on the proposal loaded and what's been discussed, you can highlight: native integration with existing tools, dedicated support included in all plans, and the volume discount mentioned earlier that applies here.",
      chatSources: ["proposal.pdf", "transcript", "web search"],
    },
  },
  differentiation: {
    label: "Why Minuta",
    heading: "This isn't another AI notetaker.",
    items: [
      {
        tag: "Deep context",
        title: "Your profile, not a generic resume read",
        description:
          "Other copilots give you Wikipedia answers. Minuta speaks from what you know, what you've built, and how you actually explain things.",
      },
      {
        tag: "Invisible",
        title: "No bots, no disruption",
        description:
          "Nothing joins your call. The other side sees nothing. It's just for you, on your screen — even during screen share.",
      },
      {
        tag: "Technical calls",
        title: "Built for people who live on technical calls",
        description:
          "Consultants, engineers, architects, sales engineers. If your job is explaining complex things live, Minuta was designed for you.",
      },
    ],
  },
  howItWorks: {
    label: "How it works",
    heading: "Three steps. Then just show up to your meetings.",
    steps: [
      {
        n: "01",
        title: "Tell Minuta who you are",
        body: "Upload your resume, projects, stack, and any docs that matter. The more context, the sharper the answers. All stored in your profile.",
      },
      {
        n: "02",
        title: "Open Minuta before the call",
        body: "Works with Zoom, Google Meet, Teams, or any platform. No bot joins the meeting. Invisible during screen share.",
      },
      {
        n: "03",
        title: "Get answers at your level",
        body: "When a question comes up, Minuta suggests a response in your tone, with your experience, in your technical domain. Glance, adapt, answer.",
      },
    ],
  },
  personas: {
    label: "Who it's for",
    heading: "Who's using Minuta.",
    items: [
      {
        title: "Technical consultants & contractors",
        description:
          "Discovery calls, architecture reviews, code walkthroughs. Minuta brings the context from your past projects to the right moment.",
      },
      {
        title: "Engineering managers & tech leads",
        description:
          "1:1s, performance reviews, planning meetings. Fewer \"let me get back to you,\" more direct answers.",
      },
      {
        title: "Sales engineers",
        description:
          "Technical demos where one question can kill a deal. Minuta keeps your battlecards at your fingertips.",
      },
    ],
  },
  privacy: {
    label: "Privacy",
    heading: "What's said on your call stays with you.",
    bullets: [
      "Core transcription runs securely with transient APIs",
      "Your data is never used to train models",
      "Delete everything with one click",
      "Optional web search, translation, account, and billing features may contact external services",
    ],
  },
  pricing: {
    label: "Plans",
    heading: "Every account starts with 7 days of full access.",
    description:
      "New signups unlock everything — copilot, summary, speaker ID — for the first 7 days. After that, Free includes 1 hour/month of secure transcription and translation. Pro unlocks unlimited recording plus copilot, summaries, speaker ID, and personal context. No audio ever leaves your computer.",
    free: {
      label: "Free",
      period: "1 hour / month",
      tagline: "7 days of full access. Then 1 hour/month of local transcription and translation.",
      features: [
        "7 days with everything unlocked — copilot, summary, speaker ID",
        "1 hour/month of secure transcription and translation after the trial",
        "Recording and transcripts stay on your computer",
        "Export to Markdown / Obsidian",
        "No credit card required",
      ],
      cta: "Start the 7-day trial",
    },
    pro: {
      label: "Pro",
      period: "/ month",
      tagline: "Keep the in-meeting copilot, summary and speaker ID after the trial. Cancel anytime.",
      badge: "Recommended",
      features: [
        "Unlimited recording and secure transcription",
        "AI copilot inside the meeting (Pro exclusive)",
        "Automatic AI summary — TL;DR, key points, action items",
        "Automatic speaker identification on every recording",
        "Personal context: load PDFs, notes, and documents",
        "Real-time web search to enrich answers",
        "Priority support",
      ],
      cta: "Subscribe to Pro",
      ctaManage: "Manage subscription",
      ctaLoading: "Opening Stripe…",
      note: "Secure billing by Stripe. Cancel anytime.",
      error: "Something went wrong opening Stripe. Try again in a moment.",
      cycleMonthly: "Monthly",
      cycleAnnual: "Annual",
      annualBilledAs: (total: string, pct: number) =>
        `Billed annually ${total} — save ${pct}%`,
      savingsBadge: (pct: number) => `−${pct}%`,
    },
    footer:
      "Prices in USD and EUR are indicative; Stripe charges in BRL. Your card issuer will convert at its daily rate. Cancel anytime, no questions asked.",
  },
  faq: {
    label: "FAQ",
    heading: "Frequently asked questions.",
    items: [
      {
        q: "What happens after the 7-day trial?",
        a: "Free keeps working with 1 hour/month of secure transcription and translation. The in-meeting copilot, automatic summary, speaker identification, personal context, and unlimited recording become Pro-only — subscribe any time to bring them back. No charges happen automatically; we never ask for a card up front.",
      },
      {
        q: "Does the other side know I'm using Minuta?",
        a: "No. Minuta doesn't join the meeting and is invisible during screen share.",
      },
      {
        q: "Does it work in Portuguese and Spanish?",
        a: "Yes. The supported production languages are English, Portuguese (pt-BR), and Spanish. Other languages are in beta.",
      },
      {
        q: "My meeting switches between English, Portuguese, and Spanish. Will Minuta help?",
        a: "Yes. Minuta supports English, Portuguese (pt-BR), and Spanish for transcription, translation, summaries, and copilot answers. The Pro copilot can draft the answer in the language you need for that moment.",
      },
      {
        q: "Does it translate live during Zoom, Teams, or Google Meet?",
        a: "Yes. Minuta captures system audio from any meeting app, transcribes English, Portuguese, and Spanish securely in the cloud, and translates between the supported languages in real time. No plugin, no bot — everything runs on your computer.",
      },
      {
        q: "Do I need to install anything in Zoom, Meet, or Teams?",
        a: "No. Minuta runs on your computer and listens to system audio — no plugins, no bots.",
      },
      {
        q: "Are my meetings recorded somewhere?",
        a: "Only if you want. By default only the transcript is stored locally on your device.",
      },
      {
        q: "Windows or Linux support?",
        a: "Mac and Windows. Linux support is on the roadmap — no date committed yet.",
      },
    ],
  },
  footerCta: {
    heading: "Ready to answer clearly in your next meeting?",
    body: "Download Minuta and use it on your next meeting. Free for 7 days, no card.",
    cta: "Download Minuta",
    ctaSub: "macOS · Windows · Linux soon",
  },
  footer: {
    tagline: "zero-retention",
    support: "Support",
    privacy: "Privacy",
    terms: "Terms",
    compareHeading: "Compare",
    useCasesHeading: "Use cases",
    resourcesHeading: "Resources",
  },
  relatedLinks: {
    comparisonsHeading: "Other honest comparisons",
    comparisonsLede: "Pick the meeting tool that actually matches your call.",
    useCasesHeading: "Other situations Minuta is built for",
    useCasesLede: "More ways professionals stay sharp on live calls across English, Portuguese, and Spanish.",
    keepExploring: "Keep exploring",
  },
  userMenu: {
    account: "My account",
    signOut: "Sign out",
  },
  account: {
    heading: "My account",
    youAreOn: "You're on the",
    plan: "plan",
    currentBadge: "Current plan",
    freeNote: "Free includes 1 hour/month of secure transcription and translation. Upgrade whenever you're ready for unlimited recording and the copilot.",
    proNote: "Thanks for supporting Minuta. The in-meeting copilot is unlocked.",
    manageSubscription: "Manage subscription",
    openingPortal: "Opening Stripe…",
    error: "Something went wrong. Try again in a moment.",
  },
  auth: {
    shell: {
      homeLabel: "Back to home",
      step: (current: number, total: number, label: string) =>
        `Step ${current}/${total} · ${label}`,
      footer:
        "By signing up you agree to the terms and privacy policy. No audio is sent to the cloud — only your email to create the account.",
    },
    login: {
      heading: "Sign in",
      subheading: "Access your Minuta account.",
      emailLabel: "Email",
      passwordLabel: "Password",
      submitting: "Signing in…",
      submit: "Sign in",
      noAccount: "Don't have an account?",
      createFree: "Create free account",
      errorEmail: "Please enter a valid email.",
      errorPassword: "Please enter your password.",
      continueGoogle: "Continue with Google",
      orDivider: "or",
      callbackBusy: "Finishing sign-in…",
      callbackError: "Couldn't complete sign-in. Try again from the home page.",
    },
    signup: {
      headingFree: "Create free account",
      headingPro: "Create Pro account",
      subFree: "No card. No charge. Download the app and get started.",
      subPro: "Let's create your access first. Stripe billing is coming soon.",
      firstNameLabel: "First name",
      lastNameLabel: "Last name",
      emailLabel: "Email",
      passwordLabel: "Password",
      passwordHint: "At least 8 characters.",
      passwordWarn: "Minimum 8 characters.",
      submitting: "Creating account…",
      submit: "Create account",
      codeNote: "We'll send a 6-digit code to your email.",
      stepLabel: "create account",
      errorEmail: "Please enter a valid email.",
      errorPassword: "Password must be at least 8 characters.",
    },
    confirm: {
      heading: "Check your email",
      subPre: "We sent a 6-digit code to",
      subPost: ".",
      yourEmail: "your email",
      codeLabel: "Confirmation code",
      submitting: "Confirming…",
      submit: "Confirm email",
      changeEmail: "← Change email",
      codeExpiry: "Code expires in 24h.",
      stepLabel: "confirm email",
      errorCode: "The code must be 6 digits.",
      errorEmail: "Email missing. Go back to signup and start again.",
    },
    done: {
      heading: "Account confirmed",
      subFree:
        "All set. Download Minuta for Mac and sign in with the email and password you just created.",
      subPro:
        "Your Pro account is set up. Stripe billing will be activated soon — until then, you're on the early access list.",
      downloadCta: "Download Minuta for macOS",
      homeCta: "Back to home",
      proNextStepsLabel: "Next steps — Pro",
      proNextSteps: [
        "For now, the app works like a Free account while we integrate Stripe.",
        "You'll receive an email when billing is active — just authorize it.",
        "Questions or bugs? Contact our support.",
      ],
      troubleNote:
        "Having trouble signing in? Try resetting your password in the app — the login screen has the link.",
    },
  },
  download: {
    eyebrow: "Private desktop app",
    heading: "Download Minuta",
    autoArm64:
      "Looks like you're on Apple Silicon. Download the highlighted macOS build, then open Minuta before your next meeting.",
    autoX64:
      "Looks like you're on an Intel Mac. Download the highlighted macOS build, then open Minuta before your next meeting.",
    autoWindows:
      "Looks like you're on Windows. The recommended installer is highlighted below.",
    autoWindowsArm64:
      "Looks like you're on Windows on ARM. The ARM64 installer is highlighted below.",
    autoUnknown:
      "Pick the build that matches your computer. No account is required to download.",
    supportedKicker: "Step 1",
    supportedHeading: "Choose your platform",
    supportedBody:
      "Minuta ships native desktop builds for macOS and Windows. The detected option is highlighted, but every build stays available for work laptops and cross-device installs.",
    macPickerLabel: "macOS",
    winPickerLabel: "Windows",
    archArm64: "Apple Silicon",
    archArm64Hint: "M1 · M2 · M3 · M4",
    archX64: "Intel Mac",
    archX64Hint: "Pre-2021 Macs",
    winSetup: "Installer (recommended)",
    winSetupHint: "Windows 10/11 · x64 · .exe",
    winPortable: "Portable",
    winPortableHint: "No install · x64 · runs from the folder",
    winSetupArm64: "Installer · ARM64",
    winSetupArm64Hint: "Windows 11 ARM · arm64 · .exe",
    winPortableArm64: "Portable · ARM64",
    winPortableArm64Hint: "No install · arm64 · runs from the folder",
    firstRunKicker: "Step 2",
    firstRunHeading: "First launch is immediate and light",
    firstRunBody:
      "Minuta runs its heavy transcription and AI models in the cloud, so there is no massive model download. First launch is instant and uses minimal resources.",
    firstRunItems: [
      "Lightweight desktop app: under 100 MB install size.",
      "Fast cloud processing: transcription and summaries run on Groq-hosted Whisper and Llama.",
      "Works with Zoom, Teams, Google Meet, and any meeting app that plays audio on your computer.",
    ],
    securityKicker: "Trust note",
    securityHeading: "No bot, zero-retention cloud pipeline",
    securityBody:
      "Minuta does not send a bot into your meeting. Audio capture stays local on your desktop, and transcription and AI run over secure, retention-limited cloud paths.",
    securityItems: [
      "No bot joins the call and nothing appears to other participants.",
      "Zero-retention: we do not store your meeting audio or transcripts on our servers, and we never train models on your data.",
      "The Mac build is signed and notarized; the Windows installer is signed for direct integration.",
    ],
    troubleshootingKicker: "If your OS blocks the app",
    troubleshootingHeading: "First-run warnings",
    troubleshootingBody:
      "These notes are below the main download because most users only need them after installing. They are expected for the current beta packaging and do not change the download flow.",
    homeCta: "Back to home",
    footnote: "macOS 13+ / Windows 10 21H2+ · signed link expires in 5 minutes",
    gatekeeperLabel: "Install on macOS",
    gatekeeperExplain:
      "Open the DMG, drag Minuta to Applications, then launch it from Finder. The Mac build is signed and notarized; no Terminal command is required.",
    winSmartScreenLabel: "If Windows blocks the installer",
    winSmartScreenExplain:
      "The Windows beta is not code-signed yet, so SmartScreen can show an \"unrecognized app\" warning on first install.",
    winSmartScreenSteps: [
      "Click More info on the SmartScreen warning.",
      "Confirm the publisher is shown as Unknown publisher.",
      "Click Run anyway to start the installer.",
    ],
    detectedBadge: "Detected",
    comingSoonBadge: "Coming soon",
    linuxPickerLabel: "Linux",
    linuxComingSoon:
      "A Linux build is on the roadmap — expect it shortly after we close the first Windows release cycle. Want an email when it lands? Sign up and we'll notify your account.",
  },
  billing: {
    done: {
      heading: "Payment complete",
      subProcessing:
        "We're activating your Pro plan. The webhook takes a few seconds to settle — this page will refresh automatically.",
      subReady:
        "Your Pro plan is active. Open Minuta and sign in again if the app is already running, so the copilot unlocks.",
      openApp: "Open Minuta",
      homeCta: "Back to home",
      refreshCta: "Refresh status",
      error: "Couldn't confirm the payment yet. Try refreshing in a moment.",
    },
    cancelled: {
      heading: "Purchase cancelled",
      sub: "No charges were made. You can subscribe any time — nothing was lost.",
      retryCta: "Try again",
      homeCta: "Back to home",
    },
    proPurchase: {
      signedOut: "Sign in to complete your Pro purchase.",
      cta: "Complete purchase",
      ctaSignIn: "Sign in to pay",
      ctaLoading: "Opening Stripe…",
      error: "Something went wrong opening Stripe. Try again in a moment.",
    },
  },
  launch: {
    hero: {
      eyebrow: "Live on Product Hunt · 2026-05-14",
      h1: "Stop freezing when the question lands.",
      sub: "A multilingual copilot for your meeting. Secure capture. No bots.",
      cta: "Get started free",
      ctaSub: "7-day full trial · no card",
      ctaSecondary: "Read the founder note",
    },
    tldr: {
      heading: "Minuta in 30 seconds",
      body:
        "Minuta is a Mac/Windows app that turns any Zoom, Teams, or Google Meet call into a real-time copilot for English, Portuguese, and Spanish meetings. It captures audio locally, runs transcription in the cloud using fast API endpoints, and drafts the next answer in the language you need at your technical level, grounded in PDFs and notes you upload. No bot joins the call. The other side sees nothing. Setup is instant with a lightweight 100 MB install (no 5 GB model downloads). Built for engineers, consultants, sales engineers, and recruiters who need to answer clearly while the meeting is still happening.",
    },
    video: {
      heading: "60 seconds, end-to-end",
      sub: "A real call. The Minuta sidebar. No edits.",
      embedUrl: "",
      caption: "If the embed doesn't load, watch on Loom →",
    },
    founderNote: {
      heading: "I'm Mark. Today is launch day — AMA.",
      body:
        "I built Minuta because I'm an ESL engineer who kept losing clients on technical calls — not because I didn't know the answer, but because I couldn't find it in English fast enough. I'm shipping it solo. I'm in the comments on Product Hunt and on email all day. Tell me what's missing — I read every reply.",
      signature: "— Mark",
    },
    discount: {
      eyebrow: "Product Hunt only · ends 2026-05-21",
      headline: "Stack HUNT30 onto the annual plan",
      price: "€67.20 / year",
      priceWas: "instead of €108",
      sub: "Annual already saves 20%. HUNT30 stacks for an extra 30% off the first year. Or use it on monthly: €6.30/mo, locked for life.",
      code: "HUNT30",
      codeLabel: "Code",
      button: "Claim the launch deal",
      buttonSub: "Apply HUNT30 at checkout",
    },
    faq: {
      heading: "Common questions about the launch",
      items: [
        {
          q: "Is Minuta open source?",
          a: "Not yet. The desktop app is closed-source today, but the heavy lifting runs on open models you can audit: Whisper for transcription, Llama / Qwen for the copilot, pyannote for diarization. The roadmap includes opening parts of the pipeline once the API stabilizes.",
        },
        {
          q: "How is privacy actually enforced?",
          a: "Audio is captured locally from your desktop, then sent through secure, zero-retention cloud APIs for transcription and AI (Groq Whisper and Llama). We do not store your audio or transcripts on our servers, and we never train models on your meetings. Only your account email is retained.",
        },
        {
          q: "Why not just use Otter or Granola?",
          a: "Otter and Granola are good cloud notetakers for teams that mainly want a transcript. Minuta is built for the moment you have to answer in a live technical conversation — in English, Portuguese, or Spanish — while the other side is still waiting. Different problem. We'll keep an honest comparison page live.",
        },
        {
          q: "What's on the roadmap?",
          a: "Public roadmap: Linux build (Q3 2026), in-app coaching (post-call feedback on your answers), team plan with shared profile, and a CLI for headless transcription. Anything else worth building, you can vote on at the public roadmap. We ship what users ask for, in order.",
        },
        {
          q: "How does the free tier and Pro trial actually work?",
          a: "Sign up — 7 days with everything unlocked, including the copilot. No card asked. After day 7, Free includes 1 hour/month of secure transcription and translation. The in-meeting copilot, summary, speaker ID, personal context, and unlimited recording move to Pro (€9/mo, or €7.50/mo billed annually). Cancel any time in Stripe.",
        },
        {
          q: "How fast is setup?",
          a: "Setup is instant. Because Minuta processes audio in the cloud, the desktop installer is under 100 MB. You can download the app, create your account, and start your first recording in under a minute. No heavy model downloads or configuration required.",
        },
      ],
    },
    banner: {
      text: "Live on Product Hunt · 30% off Pro for life with HUNT30",
      cta: "See the launch →",
      dismissAriaLabel: "Dismiss launch banner",
    },
  },
  teamsWaitlist: {
    seoTitle: "Minuta for teams — join the waitlist",
    seoDescription:
      "Shared profile, team-wide attached docs, and centralized billing. Tell us your team and we'll reach out personally when the team plan ships.",
    eyebrow: "Team plan · early access",
    heading: "A meeting copilot built for the whole team",
    subheading:
      "Right now Minuta is a per-seat Pro plan. The team plan adds a shared profile (the way your team talks about itself), a shared library of attached docs, centralized billing, and per-seat usage. Drop your work email and a one-liner about your team — Mark follows up personally as soon as it ships.",
    nameLabel: "Your name",
    namePlaceholder: "Mark Gerald",
    emailLabel: "Work email",
    emailPlaceholder: "you@company.com",
    teamSizeLabel: "How many people on the team?",
    teamSizeOptions: [
      { value: "2-5", label: "2–5" },
      { value: "6-15", label: "6–15" },
      { value: "16-50", label: "16–50" },
      { value: "50+", label: "50+" },
    ],
    notesLabel: "What does your team do? (optional)",
    notesPlaceholder: "Sales engineering at a B2B SaaS, mostly US clients…",
    cta: "Add me to the waitlist",
    ctaLoading: "Sending…",
    privacyNote:
      "We only use this to email you when the team plan ships and to ask 1–2 follow-up questions. No marketing list, no resale.",
    success: "You're on the list. Mark will email you personally before the team plan ships.",
    error: "We couldn't submit the form right now. Please try again in a moment.",
    invalidEmail: "Please enter a valid work email address.",
    afterSubmit: {
      heading: "Thanks — you're on the list.",
      body: "Mark reads every entry. Expect a personal email when the team plan is ready (and possibly a quick question or two before that).",
      ctaHome: "Back to Minuta",
      ctaPricing: "See the per-seat Pro plan",
    },
  },
};
